import { List, Map } from 'immutable';
import React, { useEffect } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { generatePath, useHistory, useParams } from 'react-router-dom';

import Icon from '../ui-components/Icon';

import { Sound } from 'flow/types/Sound';
import { routes } from 'flow/types/routes';
import Loading from 'flow/components/ui-components/Loading';
import { PbxParams } from 'flow/types/Params';
import { TypedMap } from 'flow/types/TypedMap';
import Page from 'flow/components/Setting/Page';
import useAppContext from 'flow/hooks/useAppContext';
import IconButton from '../ui-components/IconButton';
import { getWelcomeSounds } from 'flow/utils/pbx-utils';
import { MiniAudioPlayer } from 'flow/components/AudioPlayers';
import { homeActionUrlPath, isUserSoundKey } from 'flow/utils';
import { fetchDefaultSounds } from 'flow/actions/sounds-actions';
import { fetchQueue, updateQueue } from 'flow/actions/pbx-actions';
import SettingSelectableItem from '../ui-components/SettingSelectableItem';

const PbxWelcomeMessageOpen = () => {
    const dispatch = useAppDispatch();
    const history = useHistory();

    const { i18next } = useAppContext();

    const { entityKey } = useParams<PbxParams>();

    useEffect(() => {
        dispatch(fetchQueue(entityKey));
    }, [dispatch, entityKey]);

    useEffect(() => {
        dispatch(fetchDefaultSounds());
    }, [dispatch]);

    const pbxDetails = useAppSelector((state) =>
        state.pbx.getIn(['queues', entityKey], Map()),
    );
    const sounds = useAppSelector((state) => state.sounds);

    const defaultSounds = sounds.get('defaultSounds');
    const selectedSoundKey = pbxDetails.getIn(['welcomeSound', 'key']);

    if (!pbxDetails?.has('settings') || !defaultSounds) {
        return <Loading />;
    }

    const userSounds = sounds.get('sounds', List());
    const queueSounds = sounds
        ? getWelcomeSounds(pbxDetails, userSounds, defaultSounds)
        : null;

    const defaultWelcomeSounds = sounds
        ? pbxDetails.getIn(['settings', 'defaultWelcomeSounds'], Map()).first()
        : null;

    const welcomeSounds = defaultWelcomeSounds
        ? queueSounds.concat(defaultWelcomeSounds).toOrderedSet().toList()
        : null;

    const handleSound = (userSoundKey: string) => {
        const welcomeSound = isUserSoundKey(userSoundKey)
            ? { key: userSoundKey }
            : {};
        dispatch(
            updateQueue(entityKey, {
                welcomeSound,
            }),
        );

        history.push(
            generatePath(
                routes['pbx.queues.entityKey.settings.welcomemessage'],
                { entityKey },
            ),
        );
    };

    return (
        <Page
            header="Queue.Settings.List.WelcomeMessage"
            onClickBack={() =>
                history.push(
                    generatePath(
                        routes['pbx.queues.entityKey.settings.welcomemessage'],
                        {
                            entityKey,
                        },
                    ),
                )
            }
        >
            <div className="text-xs text-gray-500">
                {i18next.t(
                    'Queue.WelcomeMessage.Available.SoundPickerDescription',
                )}
            </div>

            <div className="gap-4">
                <SettingSelectableItem
                    key="none"
                    value={null}
                    onClick={() => handleSound('none')}
                    selected={selectedSoundKey}
                >
                    <div className="flex items-center gap-1">
                        <div>
                            <IconButton
                                onClick={() => handleSound('none')}
                                aria-label={i18next.t('empty.sound')}
                            >
                                <Icon iconKey="volumeOff" />
                            </IconButton>
                        </div>

                        <div>{i18next.t('empty.sound')}</div>
                    </div>
                </SettingSelectableItem>

                {welcomeSounds.map((soundDTO: TypedMap<Sound>) => {
                    if (!soundDTO.has('playbackURL')) {
                        return null;
                    }

                    const playbackUrl = homeActionUrlPath(
                        `/${soundDTO.get('playbackURL')}`,
                    );
                    return (
                        <SettingSelectableItem
                            key={soundDTO.get('key')}
                            value={soundDTO.get('key')}
                            onClick={() => handleSound(soundDTO.get('key'))}
                            selected={selectedSoundKey}
                        >
                            <div className="flex items-center gap-1">
                                <div>
                                    {playbackUrl ? (
                                        <MiniAudioPlayer
                                            url={playbackUrl}
                                            uniqueId={`user-sound-key${soundDTO.get(
                                                'key',
                                            )}`}
                                        />
                                    ) : null}
                                </div>

                                <div>{soundDTO.get('name')}</div>
                            </div>
                        </SettingSelectableItem>
                    );
                })}
            </div>
        </Page>
    );
};

export default PbxWelcomeMessageOpen;
